/**
 * DEFCAT-R2-16 — defect-drawer location cascade helpers (pure).
 *
 * The drawer's WHERE block is a cascade of dropdowns: the first is L0 (the
 * global component group), then one dropdown per deeper location level present
 * in the catalogue. These helpers build the per-level option lists scoped to a
 * chosen L0 + the parent picked above, and resolve the data-point prefill.
 *
 * The catalogue `axis='location'` entries form a forest; `componentGroupId`
 * sits on the entry attaching to L0 (the leaf for flat catalogues, the L1 group
 * for nested ones — see componentGroupScoping.ts). `depth` is the per-axis level
 * (0-based). Pure (no React) so the tree-walk is unit-tested in isolation.
 */
import type { DefectCatalogueEntryV2 } from "@aeriosiq/types";
import { effectiveComponentGroupId } from "./componentGroupScoping.js";

export interface LocationLevelOption {
  entryId: string;
  /** Display label (name.en, with first-available-language fallback). */
  label: string;
  parentEntryId: string | null;
  depth: number;
}

function nameEn(e: DefectCatalogueEntryV2): string {
  const en = e.name.en.trim();
  if (en.length > 0) return en;
  for (const key of Object.keys(e.name)) {
    const v = (e.name as Record<string, string>)[key];
    if (typeof v === "string" && v.trim().length > 0) return v.trim();
  }
  return "(unnamed)";
}

/** Location entries only, indexed by id. */
function locationEntries(
  entries: readonly DefectCatalogueEntryV2[],
): DefectCatalogueEntryV2[] {
  return entries.filter((e) => e.axis === "location");
}

/**
 * The deepest location level (0-based) the catalogue uses. Derives from `depth`
 * when present, else from the longest parent chain. Returns -1 when the
 * catalogue has no location axis. The L0 component-group selector is level -1
 * conceptually (it is NOT a catalogue location entry); the in-catalogue levels
 * start at 0.
 */
export function maxLocationDepth(
  entries: readonly DefectCatalogueEntryV2[],
): number {
  const locs = locationEntries(entries);
  if (locs.length === 0) return -1;
  const byId = new Map(locs.map((e) => [e.entryId, e] as const));
  let max = -1;
  for (const e of locs) {
    if (typeof e.depth === "number") {
      if (e.depth > max) max = e.depth;
      continue;
    }
    // Fallback: count parent hops.
    let d = 0;
    let cur: DefectCatalogueEntryV2 | undefined = e;
    const seen = new Set<string>();
    while (cur?.parentEntryId && !seen.has(cur.entryId)) {
      seen.add(cur.entryId);
      cur = byId.get(cur.parentEntryId);
      if (cur) d += 1;
    }
    if (d > max) max = d;
  }
  return max;
}

/** Depth of a single location entry (cached-friendly; mirrors maxLocationDepth). */
function depthOf(
  e: DefectCatalogueEntryV2,
  byId: ReadonlyMap<string, DefectCatalogueEntryV2>,
): number {
  if (typeof e.depth === "number") return e.depth;
  let d = 0;
  let cur: DefectCatalogueEntryV2 | undefined = e;
  const seen = new Set<string>();
  while (cur?.parentEntryId && !seen.has(cur.entryId)) {
    seen.add(cur.entryId);
    cur = byId.get(cur.parentEntryId);
    if (cur) d += 1;
  }
  return d;
}

/**
 * Options for the location dropdown at `level` (0-based catalogue depth), given
 * the chosen L0 `componentGroupId` and the entry chosen at the immediately
 * shallower level (`parentEntryId`, null for the first in-catalogue level).
 *
 *   • level 0 entries: those whose effective component group === componentGroupId
 *     (top of the location chain hangs under the chosen L0).
 *   • level n>0 entries: those at that depth whose parentEntryId === the chosen
 *     parent above.
 *
 * A null componentGroupId yields an empty list (no L0 chosen → nothing to show).
 */
export function locationOptionsAtLevel(
  entries: readonly DefectCatalogueEntryV2[],
  componentGroupId: string | null,
  level: number,
  parentEntryId: string | null,
): LocationLevelOption[] {
  if (!componentGroupId) return [];
  const locs = locationEntries(entries);
  const byId = new Map(locs.map((e) => [e.entryId, e] as const));

  const matchesGroup = (e: DefectCatalogueEntryV2): boolean =>
    effectiveComponentGroupId(e, byId) === componentGroupId;

  const out: LocationLevelOption[] = [];
  for (const e of locs) {
    if (depthOf(e, byId) !== level) continue;
    if (!matchesGroup(e)) continue;
    if (level === 0) {
      // Top in-catalogue level: any group-matching entry at depth 0.
    } else {
      if ((e.parentEntryId ?? null) !== parentEntryId) continue;
    }
    out.push({
      entryId: e.entryId,
      label: nameEn(e),
      parentEntryId: e.parentEntryId ?? null,
      depth: depthOf(e, byId),
    });
  }
  out.sort((a, b) => {
    const sa = byId.get(a.entryId)?.sortOrder;
    const sb = byId.get(b.entryId)?.sortOrder;
    if (typeof sa === "number" && typeof sb === "number" && sa !== sb)
      return sa - sb;
    return a.label.localeCompare(b.label);
  });
  return out;
}

/**
 * The root→entry ancestor path (ids) for a chosen location leaf, so the drawer
 * can backfill the cascade dropdowns to the prefilled entry. Index 0 is the
 * shallowest (depth-0) ancestor.
 */
export function locationAncestorPath(
  entries: readonly DefectCatalogueEntryV2[],
  entryId: string | null,
): string[] {
  if (!entryId) return [];
  const byId = new Map(
    locationEntries(entries).map((e) => [e.entryId, e] as const),
  );
  const path: string[] = [];
  let cur = byId.get(entryId);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.entryId)) {
    seen.add(cur.entryId);
    path.push(cur.entryId);
    cur = cur.parentEntryId ? byId.get(cur.parentEntryId) : undefined;
  }
  return path.reverse();
}

/**
 * DEFCAT-R2-16 AC3 — resolve the catalogue LOCATION entry to prefill from the
 * data point. The data point carries `location_detail_id` (a row of
 * `global_location_details`); catalogue location entries reference the same
 * table via `globalLocationDetailId`. Match them.
 *
 * Returns the matching entry id, or `null` when the data point has no detail,
 * no catalogue entry references it, or (when `componentGroupId` is given) the
 * match falls outside the chosen L0 — caller then leaves the dropdown empty.
 */
export function prefillLocationEntryId(
  entries: readonly DefectCatalogueEntryV2[],
  dataPointGlobalLocationDetailId: string | null | undefined,
  componentGroupId?: string | null,
): string | null {
  if (!dataPointGlobalLocationDetailId) return null;
  const locs = locationEntries(entries);
  const byId = new Map(locs.map((e) => [e.entryId, e] as const));
  // Prefer a leaf match (most specific), but accept any matching entry.
  const matches = locs.filter(
    (e) => e.globalLocationDetailId === dataPointGlobalLocationDetailId,
  );
  if (matches.length === 0) return null;
  const scoped = componentGroupId
    ? matches.filter(
        (e) => effectiveComponentGroupId(e, byId) === componentGroupId,
      )
    : matches;
  const pool = scoped.length > 0 ? scoped : componentGroupId ? [] : matches;
  if (pool.length === 0) return null;
  // Prefer an explicit leaf, then deepest.
  const leaf = pool.find((e) => e.isLeaf === true);
  if (leaf) return leaf.entryId;
  let best: DefectCatalogueEntryV2 | undefined;
  let bestDepth = -1;
  for (const e of pool) {
    const d = depthOf(e, byId);
    if (d > bestDepth) {
      best = e;
      bestDepth = d;
    }
  }
  return best?.entryId ?? null;
}
